/**
 * Faults that happened while the display was offline, kept until it can talk
 * again.
 *
 * Measured on the panel: pulling the network mid-playlist turns the screen
 * black at the next video, and Sentry receives NOTHING. That is not a gap in
 * the reporting, it is a property of the fault — the thing that broke the
 * screen is the same thing that stops us being told about it. Every
 * network-caused fault is invisible in real time, permanently.
 *
 * Which is most of them. The production failure on display 20 is exactly this
 * shape: a panel powering on before its network is ready.
 *
 * So when a report cannot be sent, it is written here instead and posted on the
 * next start that has connectivity. The screen was blank at 06:49; you find out
 * at 06:52 rather than never.
 */

import { captureMessage } from '@sentry/browser';

const STORAGE_KEY = 'display_deferred_faults';
// A display offline overnight should not return with hundreds of entries, and
// the oldest are the least useful. Keep a short tail.
const MAX_ENTRIES = 15;

type DeferredFault = {
  at: number;
  message: string;
  detail: Record<string, unknown>;
};

function read(): DeferredFault[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DeferredFault[]) : [];
  } catch {
    // Corrupt or unavailable storage must never break playback.
    return [];
  }
}

function write(entries: DeferredFault[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    /* full or disabled storage — nothing sensible to do */
  }
}

/**
 * Remember a fault that could not be reported now.
 *
 * Only called when the display is offline, so a fault that DID reach Sentry is
 * never duplicated here.
 */
export function deferFault(message: string, detail: Record<string, unknown>): void {
  const entries = read();
  entries.push({ at: Date.now(), message, detail });
  write(entries);
}

export function hasDeferredFaults(): boolean {
  return read().length > 0;
}

/**
 * Post anything remembered, then forget it.
 *
 * Cleared before sending rather than after: a send that fails would otherwise
 * be retried on every start forever, and a fault reported twice is a smaller
 * problem than a display that reports the same night's outage every morning.
 */
export function flushDeferredFaults(): void {
  const entries = read();

  if (entries.length === 0) {
    return;
  }

  write([]);

  for (const entry of entries) {
    try {
      const ageMs = Date.now() - entry.at;

      captureMessage(`[deferred] ${entry.message}`, {
        level: 'error',
        tags: { failureKind: 'deferred-fault' },
        extra: {
          ...entry.detail,
          // The event timestamp is when it was SENT. The fault happened
          // earlier, sometimes hours earlier, and that difference is the
          // whole point of this file.
          occurredAt: new Date(entry.at).toISOString(),
          reportedAfterMs: ageMs,
        },
      });
    } catch {
      /* one bad entry must not stop the rest */
    }
  }
}
